// ── Rules: interactive rulebook page for Lacuna ──

import {
  BOARD_RADIUS,
  ANCHOR_COUNT,
  anchorPosition,
  anchorAngle,
  strandPath,
  routeStrand,
  waypointsToSVGPath,
} from '../board/geometry';

// ── Constants ──

const SVG_NS = 'http://www.w3.org/2000/svg';

const COLOR_BG = '#FAFAF8';
const COLOR_TEXT = '#2A2A2A';
const COLOR_BLACK = '#1A1A1A';
const COLOR_RED = '#C0392B';
const COLOR_ANCHOR_LABEL = '#999';
const COLOR_MUTED = '#888';

const CIRCLE_STROKE_WIDTH = 0.06;
const ANCHOR_RADIUS = 0.1;
const ANCHOR_LABEL_FONT_SIZE = 0.35;
const ANCHOR_LABEL_RADIUS = 3.5;
const NODE_RADIUS = 0.08;
const NODE_STROKE_WIDTH = 0.05;
const STRAND_STROKE_WIDTH = 0.07;

// ── SVG helpers ──

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, String(v));
    }
  }
  return el;
}

/** Create an SVG element with the standard board viewBox for a rules diagram. */
function createDiagram(width: string): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: '-4 -4 8 8',
    xmlns: SVG_NS,
  });
  svg.style.width = width;
  svg.style.maxWidth = '100%';
  svg.style.height = 'auto';
  svg.style.display = 'block';
  svg.style.margin = '0 auto';
  return svg;
}

/** Draw the outer circle and 12 labeled anchor dots into an SVG diagram. */
function drawBoardInDiagram(svg: SVGSVGElement, opts?: { labels?: boolean }): void {
  const showLabels = opts?.labels !== false;

  // Outer circle
  const circle = svgEl('circle', {
    cx: 0,
    cy: 0,
    r: BOARD_RADIUS,
    fill: 'none',
    stroke: COLOR_TEXT,
    'stroke-width': CIRCLE_STROKE_WIDTH,
  });
  svg.appendChild(circle);

  // Anchors
  for (let i = 1; i <= ANCHOR_COUNT; i++) {
    const pos = anchorPosition(i);

    const dot = svgEl('circle', {
      cx: pos.x,
      cy: -pos.y,
      r: ANCHOR_RADIUS,
      fill: COLOR_TEXT,
    });
    dot.dataset.anchor = String(i);
    dot.classList.add('rules-anchor-dot');
    svg.appendChild(dot);

    if (showLabels) {
      const angle = anchorAngle(i);
      const lx = ANCHOR_LABEL_RADIUS * Math.cos(angle);
      const ly = ANCHOR_LABEL_RADIUS * Math.sin(angle);
      const label = svgEl('text', {
        x: lx,
        y: -ly,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        fill: COLOR_ANCHOR_LABEL,
        'font-size': ANCHOR_LABEL_FONT_SIZE,
        'font-family': "'DM Sans', system-ui, sans-serif",
      });
      label.textContent = String(i);
      label.classList.add('rules-anchor-label');
      label.dataset.anchor = String(i);
      svg.appendChild(label);
    }
  }
}

/**
 * Build a closed SVG path that follows routed spline curves between
 * a sequence of vertices (forming a closed polygon). Each edge uses
 * the exact same waypointsToSVGPath output as drawStrand, so the fill
 * boundary matches the visible strands pixel-perfectly.
 */
function buildClosedSplineFill(
  vertices: Array<{ x: number; y: number }>,
): string {
  if (vertices.length < 3) return '';

  const n = vertices.length;
  const parts: string[] = [`M ${vertices[0].x} ${-vertices[0].y}`];

  for (let i = 0; i < n; i++) {
    const from = vertices[i];
    const to = vertices[(i + 1) % n];
    const obstacles = vertices.filter((_, j) => j !== i && j !== (i + 1) % n);
    const waypoints = routeStrand(from, to, obstacles);
    // Get the SVG path and extract just the C commands (skip the M)
    const svgPath = waypointsToSVGPath(waypoints);
    const cIdx = svgPath.indexOf('C');
    if (cIdx >= 0) {
      parts.push(svgPath.slice(cIdx));
    }
  }

  parts.push('Z');
  return parts.join(' ');
}

/** Draw a strand path between two points with given color. */
function drawStrand(
  svg: SVGSVGElement,
  from: { x: number; y: number },
  to: { x: number; y: number },
  color: string,
  extraAttrs?: Record<string, string>,
  obstacles: Array<{ x: number; y: number }> = [],
): SVGPathElement {
  const d = strandPath(from, to, obstacles);
  const path = svgEl('path', {
    d,
    fill: 'none',
    stroke: color,
    'stroke-width': STRAND_STROKE_WIDTH,
    'stroke-linecap': 'round',
  });
  if (extraAttrs) {
    for (const [k, v] of Object.entries(extraAttrs)) {
      path.setAttribute(k, v);
    }
  }
  svg.appendChild(path);
  return path;
}

/** Draw a node circle at position (x, y) in math coords (y negated for SVG). */
function drawNode(
  svg: SVGSVGElement,
  x: number,
  y: number,
  opts?: { fill?: string; stroke?: string },
): SVGCircleElement {
  const circle = svgEl('circle', {
    cx: x,
    cy: -y,
    r: NODE_RADIUS,
    fill: opts?.fill ?? '#fff',
    stroke: opts?.stroke ?? COLOR_TEXT,
    'stroke-width': NODE_STROKE_WIDTH,
  });
  svg.appendChild(circle);
  return circle;
}

/** Create a styled button for interactive demos. */
function createButton(text: string, cls?: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = `rules-btn ${cls ?? ''}`.trim();
  btn.textContent = text;
  return btn;
}

/** Create an HTML element with optional className and innerHTML. */
function el(
  tag: string,
  className?: string,
  innerHTML?: string,
): HTMLElement {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (innerHTML) e.innerHTML = innerHTML;
  return e;
}

// ── Tooltip helper ──

function attachAnchorTooltips(svg: SVGSVGElement): void {
  // Create a tooltip group
  const tooltipG = svgEl('g');
  tooltipG.classList.add('rules-tooltip-group');
  tooltipG.style.pointerEvents = 'none';
  svg.appendChild(tooltipG);

  const bg = svgEl('rect', {
    rx: 0.12,
    ry: 0.12,
    fill: COLOR_TEXT,
    opacity: 0.92,
  });
  const txt = svgEl('text', {
    fill: COLOR_BG,
    'font-size': 0.28,
    'font-family': "'DM Sans', system-ui, sans-serif",
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
  });
  tooltipG.appendChild(bg);
  tooltipG.appendChild(txt);
  tooltipG.style.display = 'none';

  const dots = svg.querySelectorAll('.rules-anchor-dot');
  dots.forEach((dot) => {
    const i = Number((dot as SVGElement).dataset.anchor);
    dot.addEventListener('mouseenter', () => {
      const pos = anchorPosition(i);
      const angle = anchorAngle(i);
      const offsetR = 0.55;
      const tx = pos.x + offsetR * Math.cos(angle);
      const ty = -pos.y - offsetR * Math.sin(angle);

      txt.textContent = `Anchor ${i} (2 sockets)`;
      // Measure text and size background
      const bbox = txt.getBBox();
      const padX = 0.15;
      const padY = 0.08;
      bg.setAttribute('x', String(tx - bbox.width / 2 - padX));
      bg.setAttribute('y', String(ty - bbox.height / 2 - padY));
      bg.setAttribute('width', String(bbox.width + padX * 2));
      bg.setAttribute('height', String(bbox.height + padY * 2));
      txt.setAttribute('x', String(tx));
      txt.setAttribute('y', String(ty));

      tooltipG.style.display = '';

      // Highlight the anchor dot
      (dot as SVGElement).setAttribute('r', '0.16');
      (dot as SVGElement).setAttribute('fill', COLOR_RED);
    });

    dot.addEventListener('mouseleave', () => {
      tooltipG.style.display = 'none';
      (dot as SVGElement).setAttribute('r', String(ANCHOR_RADIUS));
      (dot as SVGElement).setAttribute('fill', COLOR_TEXT);
    });
  });
}

// ── Section builders ──

function buildBoardSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'The Board'));

  const desc = el(
    'p',
    'rules-text',
    `Lacuna is played on a circle with <strong>12 anchors</strong> evenly spaced around the circumference, numbered 1 through 12 like a clock face. Each anchor begins with <strong>2 sockets</strong> — connection points where strands can attach.`,
  );
  section.appendChild(desc);

  const hint = el(
    'p',
    'rules-hint',
    'Hover over an anchor to highlight it.',
  );
  section.appendChild(hint);

  const diagram = createDiagram('320px');
  diagram.classList.add('rules-diagram');
  drawBoardInDiagram(diagram);
  attachAnchorTooltips(diagram);
  section.appendChild(diagram);

  return section;
}

function buildStrandsSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Strands & Nodes'));

  const desc = el(
    'p',
    'rules-text',
    `A <strong>strand</strong> is a line connecting two vertices (anchors or nodes). ` +
    `A <strong>node</strong> is an interior point you place inside the circle. ` +
    `Each strand you draw consumes one socket on each endpoint. ` +
    `Nodes have a variable number of sockets determined when placed.`,
  );
  section.appendChild(desc);

  // Build diagram: node at center-ish, 2 strands to A1 and A7
  const diagram = createDiagram('320px');
  diagram.classList.add('rules-diagram');
  drawBoardInDiagram(diagram);

  const nodeX = 0;
  const nodeY = 0.6;
  const a1 = anchorPosition(1);
  const a7 = anchorPosition(7);
  const a4 = anchorPosition(4);

  drawStrand(diagram, { x: nodeX, y: nodeY }, a1, COLOR_BLACK);
  drawStrand(diagram, { x: nodeX, y: nodeY }, a7, COLOR_BLACK);
  drawNode(diagram, nodeX, nodeY);

  section.appendChild(diagram);

  // Interactive button
  const btnWrap = el('div', 'rules-btn-wrap');
  const addBtn = createButton('Add a strand', 'rules-btn-primary');
  btnWrap.appendChild(addBtn);
  section.appendChild(btnWrap);

  let added = false;
  addBtn.addEventListener('click', () => {
    if (added) return;
    added = true;
    addBtn.disabled = true;
    addBtn.textContent = 'Strand added';

    // Animate a third strand from node to A4
    const path = drawStrand(
      diagram,
      { x: nodeX, y: nodeY },
      a4,
      COLOR_RED,
    );

    // Get path length for stroke-dashoffset animation
    const len = path.getTotalLength();
    path.setAttribute('stroke-dasharray', String(len));
    path.setAttribute('stroke-dashoffset', String(len));
    path.style.transition = 'stroke-dashoffset 0.6s ease-out';

    // Force reflow then animate
    path.getBoundingClientRect();
    path.setAttribute('stroke-dashoffset', '0');

    // Re-draw node on top after strand
    drawNode(diagram, nodeX, nodeY);
  });

  const note = el(
    'p',
    'rules-hint',
    'Click the button to see an animated strand drawn from the node to anchor 4.',
  );
  section.appendChild(note);

  return section;
}

function buildTurnSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Your Turn'));

  const intro = el(
    'p',
    'rules-text',
    `Each turn has three steps:`,
  );
  section.appendChild(intro);

  const steps = el('ol', 'rules-steps');
  steps.innerHTML = `
    <li><strong>Draw tension (t)</strong> — Draw two tokens from the bag. Keep one as your tension value; return the other.</li>
    <li><strong>Choose k</strong> — Pick how many strands to draw: any value from 1 to t.</li>
    <li><strong>Place a node & draw strands</strong> — Place a new node inside a live region, then draw k strands from that node to distinct boundary vertices with free sockets. The new node receives k&nbsp;&minus;&nbsp;1 free sockets; each target vertex loses one.</li>
  `;
  section.appendChild(steps);

  const note = el(
    'p',
    'rules-text rules-note-box',
    `<strong>Not enough targets?</strong> If you place a node in a region that doesn't have k boundary vertices with free sockets, k is automatically reduced to the number available. You must always draw at least 1 strand.`,
  );
  note.innerHTML = note.textContent ?? '';
  // Re-set innerHTML to parse the HTML
  note.innerHTML =
    `<strong>Not enough targets?</strong> If you place a node in a region that doesn't have k boundary vertices with free sockets, ` +
    `k is automatically reduced to the number available. You must always draw at least 1 strand.`;
  section.appendChild(note);

  // Mini interactive demo
  const demoWrap = el('div', 'rules-demo');
  demoWrap.innerHTML = '<p class="rules-demo-label">Try it: draw tension</p>';

  const tokenRow = el('div', 'rules-token-row');
  const tensionValues = [2, 1];
  const kRow = el('div', 'rules-token-row');
  kRow.style.display = 'none';

  const resultText = el('p', 'rules-demo-result');
  resultText.style.display = 'none';

  tensionValues.forEach((t) => {
    const btn = createButton(String(t), 'rules-token');
    btn.addEventListener('click', () => {
      // Disable tension buttons
      tokenRow.querySelectorAll('button').forEach((b) => {
        (b as HTMLButtonElement).disabled = true;
        b.classList.remove('rules-token-active');
      });
      btn.classList.add('rules-token-active');

      // Show k choices
      kRow.innerHTML = '';
      const kLabel = el('span', 'rules-demo-label', `Tension = ${t}. Choose k:`);
      kRow.appendChild(kLabel);
      for (let k = 1; k <= t; k++) {
        const kBtn = createButton(String(k), 'rules-token');
        kBtn.addEventListener('click', () => {
          kRow.querySelectorAll('button').forEach((b) => {
            (b as HTMLButtonElement).disabled = true;
            b.classList.remove('rules-token-active');
          });
          kBtn.classList.add('rules-token-active');
          resultText.innerHTML =
            `You chose <strong>k = ${k}</strong>. Your new node gets <strong>${k - 1} free socket${k - 1 !== 1 ? 's' : ''}</strong>, ` +
            `and you draw <strong>${k} strand${k > 1 ? 's' : ''}</strong>.`;
          resultText.style.display = '';
        });
        kRow.appendChild(kBtn);
      }
      kRow.style.display = '';
    });
    tokenRow.appendChild(btn);
  });

  demoWrap.appendChild(tokenRow);
  demoWrap.appendChild(kRow);
  demoWrap.appendChild(resultText);
  section.appendChild(demoWrap);

  return section;
}

function buildRegionsSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Regions'));

  const desc = el(
    'p',
    'rules-text',
    `Strands divide the circle into <strong>regions</strong>. A region whose boundary is made entirely of strands (no arc of the outer circle) is called an <strong>interior region</strong>. Regions that include a piece of the circle's arc are <strong>non-interior</strong>.`,
  );
  section.appendChild(desc);

  // Build a diagram with strands forming a triangle that creates an interior region
  const diagram = createDiagram('320px');
  diagram.classList.add('rules-diagram');
  drawBoardInDiagram(diagram);

  const a1 = anchorPosition(1);
  const a5 = anchorPosition(5);
  const a9 = anchorPosition(9);

  // Triangle strands: A1-A5, A5-A9, A9-A1 (routed around each other's vertices)
  drawStrand(diagram, a1, a5, COLOR_BLACK, undefined, [a9]);
  drawStrand(diagram, a5, a9, COLOR_BLACK, undefined, [a1]);
  drawStrand(diagram, a9, a1, COLOR_BLACK, undefined, [a5]);

  // Interior region fill (the triangle) — follows spline curves
  const triPath = svgEl('path', {
    d: buildClosedSplineFill([a1, a5, a9]),
    fill: 'rgba(26,26,26,0.08)',
    stroke: 'none',
    cursor: 'pointer',
  });
  triPath.classList.add('rules-region');
  triPath.dataset.regionType = 'interior';

  // Non-interior region: the arc segment between A1 and A5 (top-right slice)
  // Approximate with an arc + straight line
  const arcRegion = svgEl('path', {
    d: `M ${a1.x} ${-a1.y} A ${BOARD_RADIUS} ${BOARD_RADIUS} 0 0 1 ${a5.x} ${-a5.y} L ${a1.x} ${-a1.y} Z`,
    fill: 'transparent',
    stroke: 'none',
    cursor: 'pointer',
  });
  arcRegion.classList.add('rules-region');
  arcRegion.dataset.regionType = 'non-interior';

  // Insert fills below strands: re-insert at beginning
  const firstChild = diagram.firstChild;
  diagram.insertBefore(arcRegion, firstChild ? firstChild.nextSibling : null);
  diagram.insertBefore(triPath, firstChild ? firstChild.nextSibling : null);

  section.appendChild(diagram);

  // Labels
  const labelInterior = el('div', 'rules-region-labels');
  labelInterior.innerHTML = `
    <span class="rules-region-tag rules-region-tag-interior">Interior region (click triangle)</span>
    <span class="rules-region-tag rules-region-tag-non-interior">Non-interior region (click arc area)</span>
  `;
  section.appendChild(labelInterior);

  const infoBox = el('p', 'rules-region-info');
  infoBox.textContent = 'Click a region in the diagram to identify it.';
  section.appendChild(infoBox);

  // Interactivity
  triPath.addEventListener('click', () => {
    triPath.setAttribute('fill', 'rgba(26,26,26,0.15)');
    arcRegion.setAttribute('fill', 'transparent');
    infoBox.innerHTML =
      '<strong>Interior region</strong> — Bounded entirely by strands. This can become a lacuna.';
  });

  arcRegion.addEventListener('click', () => {
    arcRegion.setAttribute('fill', 'rgba(192,57,43,0.10)');
    triPath.setAttribute('fill', 'rgba(26,26,26,0.08)');
    infoBox.innerHTML =
      '<strong>Non-interior region</strong> — Its boundary includes an arc of the circle. This cannot be a lacuna.';
  });

  return section;
}

function buildLacunaeSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Lacunae'));

  const desc = el(
    'p',
    'rules-text',
    `A <strong>lacuna</strong> is an interior region where every boundary strand belongs to the <strong>same player</strong>. ` +
    `When a lacuna forms, it is immediately <strong>sealed</strong> and scored.`,
  );
  section.appendChild(desc);

  const row = el('div', 'rules-lacuna-row');

  // Diagram 1: valid lacuna (monochrome black)
  const d1Wrap = el('div', 'rules-lacuna-card');
  d1Wrap.innerHTML = '<p class="rules-lacuna-label rules-lacuna-yes">Lacuna</p>';
  const d1 = createDiagram('180px');
  drawBoardInDiagram(d1, { labels: false });
  const n1a = { x: -1.0, y: 1.5 };
  const n1b = { x: 1.0, y: 1.5 };
  const n1c = { x: 0, y: 0.2 };
  // Shaded fill using routed spline boundary
  const fill1 = svgEl('path', {
    d: buildClosedSplineFill([n1a, n1b, n1c]),
    fill: 'rgba(26,26,26,0.12)',
    stroke: 'none',
  });
  d1.appendChild(fill1);
  drawStrand(d1, n1a, n1b, COLOR_BLACK, undefined, [n1c]);
  drawStrand(d1, n1b, n1c, COLOR_BLACK, undefined, [n1a]);
  drawStrand(d1, n1c, n1a, COLOR_BLACK, undefined, [n1b]);
  drawNode(d1, n1a.x, n1a.y);
  drawNode(d1, n1b.x, n1b.y);
  drawNode(d1, n1c.x, n1c.y);
  d1Wrap.appendChild(d1);
  d1Wrap.appendChild(
    el('p', 'rules-lacuna-caption', 'Interior + monochrome (all black)'),
  );
  row.appendChild(d1Wrap);

  // Diagram 2: NOT a lacuna (mixed colors)
  const d2Wrap = el('div', 'rules-lacuna-card');
  d2Wrap.innerHTML = '<p class="rules-lacuna-label rules-lacuna-no">Not a lacuna</p>';
  const d2 = createDiagram('180px');
  drawBoardInDiagram(d2, { labels: false });
  const n2a = { x: -1.0, y: 1.5 };
  const n2b = { x: 1.0, y: 1.5 };
  const n2c = { x: 0, y: 0.2 };
  drawStrand(d2, n2a, n2b, COLOR_BLACK, undefined, [n2c]);
  drawStrand(d2, n2b, n2c, COLOR_RED, undefined, [n2a]);
  drawStrand(d2, n2c, n2a, COLOR_BLACK, undefined, [n2b]);
  drawNode(d2, n2a.x, n2a.y);
  drawNode(d2, n2b.x, n2b.y);
  drawNode(d2, n2c.x, n2c.y);
  d2Wrap.appendChild(d2);
  d2Wrap.appendChild(
    el('p', 'rules-lacuna-caption', 'Interior but mixed colors (black + red)'),
  );
  row.appendChild(d2Wrap);

  // Diagram 3: NOT a lacuna (has arc)
  const d3Wrap = el('div', 'rules-lacuna-card');
  d3Wrap.innerHTML = '<p class="rules-lacuna-label rules-lacuna-no">Not a lacuna</p>';
  const d3 = createDiagram('180px');
  drawBoardInDiagram(d3, { labels: false });
  const a3_1 = anchorPosition(1);
  const a3_5 = anchorPosition(5);
  drawStrand(d3, a3_1, a3_5, COLOR_BLACK);
  // Shade the arc region
  const arcFill = svgEl('path', {
    d: `M ${a3_1.x} ${-a3_1.y} A ${BOARD_RADIUS} ${BOARD_RADIUS} 0 0 1 ${a3_5.x} ${-a3_5.y} L ${a3_1.x} ${-a3_1.y} Z`,
    fill: 'rgba(192,57,43,0.08)',
    stroke: 'none',
  });
  d3.insertBefore(arcFill, d3.querySelector('path'));
  d3Wrap.appendChild(d3);
  d3Wrap.appendChild(
    el('p', 'rules-lacuna-caption', 'Boundary includes the circle arc'),
  );
  row.appendChild(d3Wrap);

  section.appendChild(row);
  return section;
}

function buildScoringSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Scoring'));

  const desc = el(
    'p',
    'rules-text',
    `When a lacuna is sealed, the owning player scores points equal to the <strong>number of boundary strands</strong> (the perimeter length of the region).`,
  );
  section.appendChild(desc);

  const example = el(
    'p',
    'rules-text',
    `For example, a triangular lacuna scores <strong>3</strong>. A pentagonal lacuna scores <strong>5</strong>. ` +
    `The player with the highest total score at the end of the game wins.`,
  );
  section.appendChild(example);

  return section;
}

function buildEndgameSection(): HTMLElement {
  const section = el('section', 'rules-section');
  section.appendChild(el('h2', 'rules-heading', 'Game End & Pie Rule'));

  const endDesc = el(
    'p',
    'rules-text',
    `The game ends when <strong>all tokens have been drawn</strong> from the bag (or when no legal moves remain). ` +
    `The player with the most points wins. Ties are possible.`,
  );
  section.appendChild(endDesc);

  const pieDesc = el(
    'p',
    'rules-text',
    `<strong>Pie rule:</strong> After the first player completes their very first turn, ` +
    `the second player may choose to <strong>swap colors</strong> instead of taking a normal turn. ` +
    `This ensures fairness regardless of who goes first.`,
  );
  section.appendChild(pieDesc);

  return section;
}

// ── Main export ──

export function renderRules(container: HTMLElement, onBack: () => void): void {
  container.innerHTML = '';

  const page = document.createElement('div');
  page.className = 'rules-page';

  // Header
  const header = el('header', 'rules-header');
  const backBtn = createButton('\u2190 Back', 'rules-back-btn');
  backBtn.addEventListener('click', onBack);
  header.appendChild(backBtn);
  const title = el('h1', 'rules-title', 'How to Play');
  header.appendChild(title);
  page.appendChild(header);

  // Sections
  page.appendChild(buildBoardSection());
  page.appendChild(buildStrandsSection());
  page.appendChild(buildTurnSection());
  page.appendChild(buildRegionsSection());
  page.appendChild(buildLacunaeSection());
  page.appendChild(buildScoringSection());
  page.appendChild(buildEndgameSection());

  // Inject scoped styles
  const style = document.createElement('style');
  style.textContent = rulesCSS();
  page.appendChild(style);

  container.appendChild(page);
}

// ── Scoped CSS ──

function rulesCSS(): string {
  return `
.rules-page {
  background: ${COLOR_BG};
  color: ${COLOR_TEXT};
  font-family: 'DM Sans', system-ui, sans-serif;
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
  line-height: 1.65;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2.5rem;
}

.rules-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 0;
}

.rules-back-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 0.85rem;
  color: ${COLOR_TEXT};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
}
.rules-back-btn:hover {
  background: #eee;
  border-color: #ccc;
}

.rules-section {
  margin-bottom: 3rem;
}

.rules-heading {
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  letter-spacing: 0.01em;
}

.rules-text {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  color: ${COLOR_TEXT};
}

.rules-hint {
  margin: 0.5rem 0 1rem;
  font-size: 0.82rem;
  color: ${COLOR_MUTED};
  font-style: italic;
}

.rules-diagram {
  margin: 1.25rem auto;
  cursor: default;
}

.rules-anchor-dot {
  cursor: pointer;
  transition: r 0.15s, fill 0.15s;
}

.rules-steps {
  margin: 0 0 1rem;
  padding-left: 1.4rem;
  font-size: 0.95rem;
}
.rules-steps li {
  margin-bottom: 0.6rem;
}

/* Buttons */
.rules-btn {
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 0.85rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0.4rem 1rem;
  background: #fff;
  color: ${COLOR_TEXT};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.2s;
}
.rules-btn:hover:not(:disabled) {
  background: #f0f0ee;
  border-color: #ccc;
}
.rules-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.rules-btn-primary {
  background: ${COLOR_TEXT};
  color: ${COLOR_BG};
  border-color: ${COLOR_TEXT};
}
.rules-btn-primary:hover:not(:disabled) {
  background: #444;
}

.rules-btn-wrap {
  text-align: center;
  margin: 1rem 0 0.5rem;
}

/* Token buttons for tension demo */
.rules-token {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0.35rem;
}
.rules-token-active {
  background: ${COLOR_TEXT} !important;
  color: ${COLOR_BG} !important;
  border-color: ${COLOR_TEXT} !important;
}

.rules-demo {
  background: #f3f3f1;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin: 1.25rem 0;
}
.rules-demo-label {
  font-size: 0.85rem;
  font-weight: 500;
  margin: 0 0 0.6rem;
  color: ${COLOR_MUTED};
}
.rules-demo-result {
  font-size: 0.9rem;
  margin: 0.75rem 0 0;
}

.rules-token-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

/* Region labels */
.rules-region-labels {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 0.75rem 0;
}
.rules-region-tag {
  font-size: 0.78rem;
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  font-weight: 500;
}
.rules-region-tag-interior {
  background: rgba(26,26,26,0.08);
  color: ${COLOR_TEXT};
}
.rules-region-tag-non-interior {
  background: rgba(192,57,43,0.08);
  color: ${COLOR_RED};
}
.rules-region-info {
  font-size: 0.88rem;
  color: ${COLOR_MUTED};
  min-height: 1.4em;
  margin: 0;
  transition: color 0.2s;
}

/* Lacunae comparison row */
.rules-lacuna-row {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 1.25rem 0;
}
.rules-lacuna-card {
  flex: 1 1 160px;
  max-width: 200px;
  text-align: center;
}
.rules-lacuna-label {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin: 0 0 0.4rem;
}
.rules-lacuna-yes {
  color: ${COLOR_BLACK};
}
.rules-lacuna-no {
  color: ${COLOR_RED};
}
.rules-lacuna-caption {
  font-size: 0.78rem;
  color: ${COLOR_MUTED};
  margin: 0.4rem 0 0;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 480px) {
  .rules-page {
    padding: 1.25rem 1rem 3rem;
  }
  .rules-title {
    font-size: 1.25rem;
  }
  .rules-lacuna-row {
    gap: 0.75rem;
  }
  .rules-lacuna-card {
    flex: 1 1 120px;
    max-width: 160px;
  }
}
`;
}
